import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, Trash2, Image as ImageIcon, Search, RefreshCw, Info } from "lucide-react";

const NEWS_CATEGORIES = [
  "", "World", "Politics", "Business", "Technology", "Science", "Health",
  "Environment", "Entertainment", "Sports", "Good News", "Odd News",
  "Weather", "War", "Disasters", "News You Can Use",
];
const ORIENTATIONS = ["4x3", "1x1", "16x9", "any"];
const MAX_MB = 3;

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = reject;
    r.onload = () => resolve(r.result);
    r.readAsDataURL(file);
  });
}

function resolveUrl(u) {
  if (!u) return u;
  if (/^https?:\/\//i.test(u) || u.startsWith("data:")) return u;
  if (u.startsWith("/api/")) return (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + u;
  return u;
}

export default function AdminNewsIllustrations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");

  // Upload form state
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [tagsText, setTagsText] = useState("");
  const [category, setCategory] = useState("");
  const [orientation, setOrientation] = useState("4x3");
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (activeFilter === "active") params.active = true;
    if (activeFilter === "hidden") params.active = false;
    http.get("/admin/news-illustrations", { params })
      .then((r) => setItems(r.data.items || []))
      .catch(() => toast.error("Could not load illustrations"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeFilter]);

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`File exceeds ${MAX_MB} MB limit`);
      return;
    }
    setFile(f);
    const dataUrl = await fileToBase64(f);
    setPreviewUrl(dataUrl);
  };

  const upload = async () => {
    if (!file || !previewUrl) {
      toast.error("Pick an image first");
      return;
    }
    setUploading(true);
    try {
      const tags = tagsText.split(",").map((t) => t.trim()).filter(Boolean);
      const body = {
        image_base64: previewUrl,
        mime: file.type || "image/png",
        tags,
        category: category || null,
        orientation,
        active: true,
        note: note || null,
      };
      await http.post("/admin/news-illustrations", body);
      toast.success("Illustration uploaded");
      setFile(null); setPreviewUrl(""); setTagsText(""); setNote("");
      setCategory(""); setOrientation("4x3");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeOne = async (id) => {
    if (!window.confirm("Delete this illustration? This cannot be undone.")) return;
    try {
      await http.delete(`/admin/news-illustrations/${id}`);
      setItems((xs) => xs.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const toggleActive = async (it) => {
    try {
      const r = await http.patch(`/admin/news-illustrations/${it.id}`, { active: !it.active });
      setItems((xs) => xs.map((x) => (x.id === it.id ? r.data : x)));
    } catch {
      toast.error("Update failed");
    }
  };

  const filtered = useMemo(() => {
    if (!q) return items;
    const s = q.toLowerCase();
    return items.filter((it) =>
      (it.tags || []).some((t) => t.includes(s)) ||
      (it.category || "").toLowerCase().includes(s) ||
      (it.note || "").toLowerCase().includes(s),
    );
  }, [q, items]);

  const activeCount = items.filter((i) => i.active).length;

  return (
    <div data-testid="admin-news-illustrations" className="space-y-6">
      <div className="rounded-2xl border p-5" style={{ borderColor: "var(--wofy-line)", background: "var(--wofy-paper)" }}>
        <div className="flex items-center gap-3 mb-2">
          <ImageIcon className="w-5 h-5" style={{ color: "var(--wofy-primary)" }} />
          <h2 className="font-display text-2xl">News illustration library</h2>
        </div>
        <p className="text-sm mb-4" style={{ color: "var(--wofy-text-soft)" }}>
          Upload AI-generated illustrations or editorial art and tag them so the system can use them as fallback images
          when a news article has no photo. Match happens on <b>category</b> first, then <b>tags</b>, then a soft match of the
          headline. Static category SVGs are always used as a final fallback — this library only <i>enhances</i> them.
        </p>
        <div className="flex items-start gap-2 text-xs p-3 rounded-lg" style={{ background: "#FEF3C7", color: "#92400E" }}>
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <b>Tip:</b> Use lowercase, comma-separated tags describing the subject and mood — <span className="font-mono">hurricane, ocean, storm, coastal</span>.
            The more specific the tags, the better the auto-match. Aspect <b>4:3</b> works for the home strip and news list cards; <b>1:1</b> works well for thumbnails.
          </div>
        </div>
      </div>

      {/* Upload form */}
      <div className="rounded-2xl border p-5" style={{ borderColor: "var(--wofy-line)" }}>
        <h3 className="font-semibold mb-4 flex items-center gap-2"><UploadCloud className="w-4 h-4" /> Upload new illustration</h3>
        <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-6">
          <label
            data-testid="illustration-upload-drop"
            className="block border-2 border-dashed rounded-xl p-4 cursor-pointer hover:opacity-90 text-center"
            style={{ borderColor: "var(--wofy-line)" }}
          >
            {previewUrl ? (
              <img src={previewUrl} alt="preview" className="max-h-[180px] mx-auto rounded" />
            ) : (
              <div className="py-8">
                <UploadCloud className="w-8 h-8 mx-auto mb-2" style={{ color: "var(--wofy-primary)" }} />
                <div className="text-sm font-medium">Click or drop an image</div>
                <div className="text-xs mt-1" style={{ color: "var(--wofy-text-soft)" }}>PNG, JPG, WebP or SVG · up to {MAX_MB} MB</div>
              </div>
            )}
            <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={onFileChange} data-testid="illustration-upload-input" />
          </label>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: "var(--wofy-text-soft)" }}>Tags (comma-separated) *</label>
              <input
                type="text"
                value={tagsText}
                onChange={(e) => setTagsText(e.target.value)}
                placeholder="hurricane, storm, rain, coastal"
                className="w-full px-3 py-2 border rounded-lg text-sm"
                style={{ borderColor: "var(--wofy-line)" }}
                data-testid="illustration-tags-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: "var(--wofy-text-soft)" }}>Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  style={{ borderColor: "var(--wofy-line)" }}
                  data-testid="illustration-category-select"
                >
                  <option value="">(any category)</option>
                  {NEWS_CATEGORIES.filter(Boolean).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: "var(--wofy-text-soft)" }}>Aspect ratio</label>
                <select
                  value={orientation}
                  onChange={(e) => setOrientation(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  style={{ borderColor: "var(--wofy-line)" }}
                  data-testid="illustration-orientation-select"
                >
                  {ORIENTATIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: "var(--wofy-text-soft)" }}>Note (optional)</label>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Generated with Midjourney v6 — sepia palette"
                className="w-full px-3 py-2 border rounded-lg text-sm"
                style={{ borderColor: "var(--wofy-line)" }}
                data-testid="illustration-note-input"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={upload}
                disabled={uploading || !file}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold disabled:opacity-40"
                style={{ background: "var(--wofy-primary)", color: "white" }}
                data-testid="illustration-upload-btn"
              >
                <UploadCloud className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload illustration"}
              </button>
              {previewUrl && (
                <button
                  type="button"
                  onClick={() => { setFile(null); setPreviewUrl(""); }}
                  className="text-sm px-3 py-2 rounded-full border"
                  style={{ borderColor: "var(--wofy-line)" }}
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Library */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--wofy-text-soft)" }} />
          <input
            type="text"
            placeholder="Search tags, category, notes…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 pr-3 py-2 border rounded-full text-sm w-full"
            style={{ borderColor: "var(--wofy-line)" }}
            data-testid="illustration-search-input"
          />
        </div>
        <div className="flex items-center gap-1">
          {["all", "active", "hidden"].map((v) => (
            <button
              key={v}
              onClick={() => setActiveFilter(v)}
              className="px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide border"
              style={{
                borderColor: "var(--wofy-line)",
                background: activeFilter === v ? "var(--wofy-primary)" : "transparent",
                color: activeFilter === v ? "white" : "var(--wofy-text)",
              }}
              data-testid={`illustration-filter-${v}`}
            >
              {v}
            </button>
          ))}
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-full border ml-auto"
          style={{ borderColor: "var(--wofy-line)" }}
          title="Reload"
          data-testid="illustration-reload-btn"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Reload
        </button>
        <span className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>{activeCount} active · {items.length} total</span>
      </div>

      {loading ? (
        <div className="py-16 text-center text-sm" style={{ color: "var(--wofy-text-soft)" }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center border rounded-lg" style={{ borderColor: "var(--wofy-line)", color: "var(--wofy-text-soft)" }}>
          No illustrations yet. Use the form above to upload the first one.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((it) => (
            <div key={it.id} data-testid={`illustration-card-${it.id}`} className="rounded-lg border overflow-hidden flex flex-col" style={{ borderColor: "var(--wofy-line)" }}>
              <div className="aspect-[4/3] bg-[var(--wofy-card-bg,#f2ede4)] overflow-hidden flex items-center justify-center">
                <img src={resolveUrl(it.url)} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="p-3 text-xs space-y-1.5 flex-1 flex flex-col">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold uppercase tracking-wide" style={{ color: "var(--wofy-primary)" }}>
                    {it.category || "any category"}
                  </span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded border" style={{ borderColor: "var(--wofy-line)" }}>{it.orientation}</span>
                </div>
                {it.tags && it.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {it.tags.slice(0, 6).map((t) => (
                      <span key={t} className="px-1.5 py-0.5 rounded bg-[var(--wofy-card-bg,#f2ede4)] text-[10px]">{t}</span>
                    ))}
                    {it.tags.length > 6 && <span className="text-[10px] opacity-60">+{it.tags.length - 6}</span>}
                  </div>
                )}
                {it.note && <div className="text-[11px] opacity-75 italic">{it.note}</div>}
                <div className="text-[10px] opacity-60">
                  Used {it.usage_count || 0}× · {Math.round((it.size || 0) / 1024)} KB
                </div>
                <div className="flex items-center gap-2 mt-auto pt-2">
                  <button
                    onClick={() => toggleActive(it)}
                    className="text-xs px-2 py-1 rounded-full border"
                    style={{
                      borderColor: "var(--wofy-line)",
                      background: it.active ? "var(--wofy-primary)" : "transparent",
                      color: it.active ? "white" : "var(--wofy-text)",
                    }}
                    data-testid={`illustration-toggle-${it.id}`}
                  >
                    {it.active ? "Active" : "Hidden"}
                  </button>
                  <button
                    onClick={() => removeOne(it.id)}
                    className="ml-auto text-xs inline-flex items-center gap-1 px-2 py-1 rounded-full border"
                    style={{ borderColor: "var(--wofy-line)", color: "#B24040" }}
                    data-testid={`illustration-delete-${it.id}`}
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
